import React, { useRef } from 'react';

import { useIsBreakpointSize } from 'app/shared/utils';
import useScrollDirection from 'app/shared/utils/useScrollDirection';
import { Box } from 'app/shared/components/atoms';
import {
  Breadcrumb,
  Breadcrumbs,
  PageBanner,
  PageBannerProps,
} from 'app/shared/components/molecules';
import Footer from 'app/shared/components/organisms/Footer';
import Navbar, { NavbarProps } from 'app/shared/components/organisms/Navbar';

export interface MainLayoutTemplateProps {
  children: any;
  navbarProps?: NavbarProps;
  breadcrumbProps?: Breadcrumb[];
  renderAboveNavComponent?: () => void;
  isLandingPage?: boolean;
  useMinimalFooter?: boolean;
  centerContent?: boolean;
  authContextFinishedLoading: boolean;
  bannerMessage?: PageBannerProps;
  displayBannerMessage: boolean;
  handleBannerDismiss: () => void;
  'data-qaid'?: string;
}

export const MainLayoutTemplate: React.FC<
  React.PropsWithChildren<MainLayoutTemplateProps>
> = ({
  children,
  navbarProps,
  breadcrumbProps,
  renderAboveNavComponent,
  isLandingPage = false,
  useMinimalFooter = false,
  centerContent = false,
  bannerMessage,
  authContextFinishedLoading,
  displayBannerMessage,
  handleBannerDismiss,
  'data-qaid': dataQaid = 'main-layout',
  ...rest
}) => {
  const { isMobile, isDesktop } = useIsBreakpointSize();
  const scrollDirection = useScrollDirection();
  const stickyContainerRef = useRef<HTMLDivElement>(null);
  const stickyContainerHeight = stickyContainerRef?.current?.clientHeight || 0;
  const stickyContainerTop =
    isMobile && scrollDirection === 'down'
      ? `-${stickyContainerHeight}px`
      : '0';
  const footerRef = useRef<HTMLDivElement>(null);
  const footerHeight = footerRef?.current?.clientHeight || 0;

  return (
    <Box
      data-qaid={dataQaid}
      {...rest}
      minHeight="100vh"
      position="relative"
      overflowX={isDesktop ? 'unset' : 'hidden'}
    >
      <Box
        position={isMobile ? 'sticky' : 'relative'}
        top={stickyContainerTop}
        ref={stickyContainerRef}
        zIndex={100}
        w="100%"
        transition="top 400ms cubic-bezier(0.3, 0, 0.2, 1)"
      >
        {renderAboveNavComponent && renderAboveNavComponent()}
        {displayBannerMessage && bannerMessage && (
          <PageBanner
            text={bannerMessage.text}
            link={bannerMessage.link}
            linkText={bannerMessage.linkText}
            onBannerDismiss={handleBannerDismiss}
          />
        )}
        <Navbar
          isLandingPage={isLandingPage}
          {...navbarProps}
          invertColors={true}
        />
        {breadcrumbProps && <Breadcrumbs breadcrumbLinks={breadcrumbProps} />}
      </Box>
      <Box
        data-qaid="main-container"
        minHeight={`calc(100vh - ${stickyContainerHeight + footerHeight}px)`}
        w="100%"
        flexDirection={centerContent ? 'column' : undefined}
        alignItems={centerContent ? 'center' : undefined}
      >
        {children}
      </Box>
      {authContextFinishedLoading && (
        <Footer minimal={useMinimalFooter} footerRef={footerRef} />
      )}
    </Box>
  );
};
